import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/query-client";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { Mail, ExternalLink, Clock, AlertTriangle } from "lucide-react";

export interface ActiveWatch {
  id: string;
  claimId: string;
  watchedStatus: string;
  status: string;
  reminderDueAt: string;
  reminderCount: number | null;
  claimNumber: string | null;
  policyholderFirstName: string | null;
  policyholderLastName: string | null;
  policyholderEmail: string | null;
  contractorFirstName: string | null;
  contractorLastName: string | null;
  contractorEmail: string | null;
}

interface ActiveWatchesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  watches: ActiveWatch[];
}

interface EmailDialogState {
  watchId: string;
  recipientEmail: string;
  recipientName: string;
  recipientType: "policyholder" | "contractor";
  customMessage: string;
}

const DEFAULT_MESSAGE = (name: string) =>
  `Hello ${name},\n\nWe are emailing you in order to update our files.\nWe are inquiring as to the status of the repairs. Please let us know the percentage and when you expect the repairs to be completed.\nThis could be an automated message. Please disregard or advise if we made a mistake in this request.\n\nRespectfully,\n\nThe Mission Property Loss Consultants Team`;

export function ActiveWatchesDrawer({ open, onOpenChange, watches }: ActiveWatchesDrawerProps) {
  const { toast } = useToast();
  const [emailDialog, setEmailDialog] = useState<EmailDialogState | null>(null);

  const sendMutation = useMutation({
    mutationFn: async (data: EmailDialogState) => {
      return apiRequest('POST', `/api/repair-followups/${data.watchId}/send-progress-request`, {
        recipientEmail: data.recipientEmail,
        recipientName: data.recipientName,
        recipientType: data.recipientType,
        customMessage: data.customMessage || undefined,
      });
    },
    onSuccess: () => {
      toast({ title: "Email Sent", description: "Progress request email sent successfully." });
      setEmailDialog(null);
      queryClient.invalidateQueries({ queryKey: ["/api/repair-followups"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function openEmailDialog(watch: ActiveWatch, type: "policyholder" | "contractor") {
    const name = type === "contractor"
      ? `${watch.contractorFirstName || ""} ${watch.contractorLastName || ""}`.trim()
      : `${watch.policyholderFirstName || ""} ${watch.policyholderLastName || ""}`.trim();
    const email = type === "contractor" ? (watch.contractorEmail || "") : (watch.policyholderEmail || "");
    setEmailDialog({
      watchId: watch.id,
      recipientEmail: email,
      recipientName: name,
      recipientType: type,
      customMessage: DEFAULT_MESSAGE(name),
    });
  }

  const now = new Date();

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="active-watches-drawer">
          <SheetHeader>
            <SheetTitle>Active Repair Watches</SheetTitle>
            <SheetDescription>
              Claims currently in a repair follow-up status requiring progress updates.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-3">
            {watches.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No active repair watches.</p>
            )}
            {watches.map((watch) => {
              const isOverdue = new Date(watch.reminderDueAt) < now;
              const phName = `${watch.policyholderFirstName || ""} ${watch.policyholderLastName || ""}`.trim() || "Unknown";
              const hasContractor = !!watch.contractorEmail;

              return (
                <div
                  key={watch.id}
                  className="border rounded-lg p-3 space-y-2"
                  data-testid={`watch-row-${watch.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <Link
                        href={`/claims/${watch.claimId}`}
                        className="font-medium text-sm hover:underline text-blue-700 dark:text-blue-300 flex items-center gap-1"
                        data-testid={`watch-ph-link-${watch.id}`}
                        onClick={() => onOpenChange(false)}
                      >
                        {phName}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                      {watch.claimNumber && (
                        <p className="text-xs text-muted-foreground">Claim #{watch.claimNumber}</p>
                      )}
                    </div>
                    <Badge
                      className={isOverdue
                        ? "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 flex items-center gap-1"
                        : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 flex items-center gap-1"
                      }
                      data-testid={`watch-status-badge-${watch.id}`}
                    >
                      {isOverdue ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                      {isOverdue ? "Overdue" : "Watching"}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEmailDialog(watch, "policyholder")}
                      disabled={!watch.policyholderEmail}
                      data-testid={`btn-email-ph-${watch.id}`}
                    >
                      <Mail className="h-3 w-3 mr-1" />
                      Email PH
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEmailDialog(watch, "contractor")}
                      disabled={!hasContractor}
                      data-testid={`btn-email-contractor-${watch.id}`}
                    >
                      <Mail className="h-3 w-3 mr-1" />
                      Email Contractor
                    </Button>
                    {watch.reminderCount != null && watch.reminderCount > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {watch.reminderCount} reminder{watch.reminderCount !== 1 ? "s" : ""} sent
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {emailDialog && (
        <Dialog open={!!emailDialog} onOpenChange={(open) => { if (!open) setEmailDialog(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Send Progress Request Email</DialogTitle>
              <DialogDescription>
                Send a repair progress request to {emailDialog.recipientType === "contractor" ? "the contractor" : "the policyholder"}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="drawer-recipient-name">Recipient Name</Label>
                <Input
                  id="drawer-recipient-name"
                  value={emailDialog.recipientName}
                  onChange={(e) => setEmailDialog(prev => prev ? { ...prev, recipientName: e.target.value } : null)}
                  data-testid="drawer-input-recipient-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="drawer-recipient-email">Email Address</Label>
                <Input
                  id="drawer-recipient-email"
                  type="email"
                  value={emailDialog.recipientEmail}
                  onChange={(e) => setEmailDialog(prev => prev ? { ...prev, recipientEmail: e.target.value } : null)}
                  data-testid="drawer-input-recipient-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="drawer-custom-message">Message</Label>
                <Textarea
                  id="drawer-custom-message"
                  value={emailDialog.customMessage}
                  onChange={(e) => setEmailDialog(prev => prev ? { ...prev, customMessage: e.target.value } : null)}
                  rows={8}
                  data-testid="drawer-textarea-message"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialog(null)} data-testid="drawer-btn-cancel-email">
                Cancel
              </Button>
              <Button
                onClick={() => emailDialog && sendMutation.mutate(emailDialog)}
                disabled={!emailDialog?.recipientEmail || !emailDialog?.recipientName || sendMutation.isPending}
                data-testid="drawer-btn-submit-email"
              >
                <Mail className="h-4 w-4 mr-1" />
                {sendMutation.isPending ? "Sending..." : "Send Email"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
